import { useContext, useEffect, useMemo, useState } from 'react';

import { CardModal } from 'components/card-modal';
import { BrazeContext } from 'state/braze';
import {
  filterContentCardsByServiceMode,
  getReadContentCardsFromLocalStorage,
  setReadContentCardsInLocalStorage,
} from 'state/braze/hooks/utils';
import { actions, selectors, useAppDispatch, useAppSelector } from 'state/global-state';
import { t } from 'state/intl/translations';
import { useServiceModeLive } from 'state/service-mode';
import Braze, { ContentCard } from 'utils/braze';

import { CustomContentCard } from './content-cards';
import {
  CenteredLine,
  EmptyInboxCaption,
  EmptyInboxTitle,
  StyledPicture,
  UnreadSeparator,
  UnreadSeparatorText,
} from './styled';

export const NotificationInbox = () => {
  const dispatch = useAppDispatch();
  const contentCards = useAppSelector(selectors.loyalty.selectNewsFeedContentCards);
  const serviceMode = useServiceModeLive();
  const [unreadContentCardsIds, setUnreadContentCardsIds] = useState<string[]>([]);
  const { logContentCardImpression } = useContext(BrazeContext);

  const visibleContentCards = useMemo(
    () => filterContentCardsByServiceMode(contentCards, serviceMode),
    [contentCards, serviceMode]
  );

  useEffect(() => {
    if (visibleContentCards.length === 0) {
      return;
    }

    const readCardsFromLocalStorage = getReadContentCardsFromLocalStorage();
    const unreadCardsIds = visibleContentCards
      .filter(({ id, viewed }) => !viewed && !readCardsFromLocalStorage.includes(id))
      .map(({ id }) => id);

    if (unreadCardsIds.length > 0) {
      setUnreadContentCardsIds(prevState => {
        return [...new Set([...prevState, ...unreadCardsIds])];
      });
    }

    let hasUnreadVisibleCards = false;
    visibleContentCards.forEach(({ id, viewed }) => {
      if (!viewed) {
        hasUnreadVisibleCards = true;
        setReadContentCardsInLocalStorage([id]);
        logContentCardImpression(id);
      }
    });
    if (hasUnreadVisibleCards) {
      dispatch(actions.loyalty.refreshUnreadContentCards(serviceMode));
    }
  }, [
    dispatch,
    logContentCardImpression,
    serviceMode,
    unreadContentCardsIds.length,
    visibleContentCards,
  ]);

  const orderedContentCards = useMemo(
    () =>
      visibleContentCards.reduce(
        (acc, cur) => {
          const key = unreadContentCardsIds.includes(cur.id) ? 'unread' : 'read';
          acc[key].push(cur);
          return acc;
        },
        { read: [] as ContentCard[], unread: [] as ContentCard[] }
      ),
    [unreadContentCardsIds, visibleContentCards]
  );

  useEffect(() => {
    return () => Braze.requestContentCardsRefresh();
  }, []);

  return (
    <CardModal scrollView isFullScreen dismissType="back" headerText={t('news')}>
      {visibleContentCards.length === 0 ? (
        <>
          <StyledPicture source={require('./empty-state-image.png')} />
          <EmptyInboxTitle>{t('emptyInboxTitle')}</EmptyInboxTitle>
          <EmptyInboxCaption>{t('emptyInboxCaption')}</EmptyInboxCaption>
        </>
      ) : (
        <>
          {orderedContentCards.unread.map(card => (
            <CustomContentCard key={card.id} card={card} />
          ))}
          <UnreadSeparator>
            <CenteredLine />
            <UnreadSeparatorText>{t('contentCardsSeparatorLabel')}</UnreadSeparatorText>
            <CenteredLine />
          </UnreadSeparator>
          {orderedContentCards.read.map(card => (
            <CustomContentCard key={card.id} card={card} />
          ))}
        </>
      )}
    </CardModal>
  );
};
